import { autoinject } from 'aurelia-dependency-injection';
import { I18N } from 'aurelia-i18n';
import { ClassicFormsApi } from '~/api/classic-forms-api';
import {
	ConfigurationItems,
	ConfigurationItemsField,
} from '../models/generated/entities/ConfigurationItems';
import { StringUtils } from './string-utils';
import { GenericApi } from '~/api/generic-api';

@autoinject
export default class I18nConfigLabelService {
	private browserLocale: string =
		navigator.language?.substring(0, 2) || 'en-AU';
	/**
	 * namespace of translation json file that we want to replace configurable string
	 */
	public static defaultNameSpace = 'common-strings';

	private replacedNameSpace = new Set();

	constructor(private classicFormApi: ClassicFormsApi) {
		this.classicFormApi = classicFormApi;
	}

	/**
	 * Keys of the map will be replaced with values from the configuration table
	 */
	private fieldLabelMap = new Map([
		['Client', ConfigurationItemsField.CFz_LL_Client],
		['Division', ConfigurationItemsField.CFz_LL_Division],
		['Payroll', ConfigurationItemsField.CFz_LL_Payroll],
		['Fund', ConfigurationItemsField.CFz_LL_Fund],
		['Member', ConfigurationItemsField.CFz_LL_Member],
	]);

	private async loadConfigurationItemsLabel(): Promise<ConfigurationItems> {
		// getConfigItem API need division to be present. In login page, we don't have division
		// in the url and it will return 404 error.
		if (GenericApi.getDivision()) {
			return await this.classicFormApi.getConfigItem();
		}
		return null;
	}

	/**
	 * replace configurale strings in translation json file with alias
	 * from configuration table
	 * @param i18n aurealia-i18n instance
	 * @return Promise of true if the replacement process does happen else false
	 */
	public async replaceI18nStringsWithConfigLabel(
		i18n: I18N,
		nameSpace: string,
	): Promise<boolean> {
		if (this.replacedNameSpace.has(nameSpace)) return false;
		const resource = i18n.i18next.getResourceBundle(
			this.browserLocale,
			nameSpace,
		);
		const configItems = await this.loadConfigurationItemsLabel();
		if (configItems === null) return false;
		const patternReplaceMap = new Map();
		this.fieldLabelMap.forEach((value, key) => {
			patternReplaceMap.set(key, configItems[value]?.trim());
		});
		this.recursivelyReplaceResourceBundle(
			resource,
			(translationString: string) => {
				return this.replaceStringEscapingInterpolation(
					translationString,
					patternReplaceMap,
				);
			},
		);
		this.replacedNameSpace.add(nameSpace);
		return true;
	}

	/**
	 * replace searchStrings with replaceStrings, except that in double bracket {{}}
	 * @param originalString string to be processed
	 * @param searchString string to be replaced
	 * @param replaceString string to replace searchString
	 * @return processed string
	 */
	public replaceStringEscapingInterpolation(
		originalString: string,
		patternReplaceMap: Map<string, string>,
	): string {
		let result = originalString;
		patternReplaceMap.forEach((replaceValue, pattern) => {
			const regex = new RegExp(
				`(\\b${pattern}\\b)(?!([^({{)]+)?}})`,
				'ig',
			);
			result = result.replace(
				regex,
				this.matchingCaseReplacer(replaceValue),
			);
		});
		return result;
	}

	private recursivelyReplaceResourceBundle(resource, replaceCallback): void {
		for (const key in resource) {
			if (typeof resource[key] === 'string') {
				if (this.shouldMakeReplacement(key))
					resource[key] = replaceCallback(resource[key]);
			} else {
				this.recursivelyReplaceResourceBundle(
					resource[key],
					replaceCallback,
				);
			}
		}
	}

	private shouldMakeReplacement(key: string): boolean {
		return !key.endsWith('--exact');
	}

	/**
	 * We only handle 3 cases here. All uppercases, all lowercases and capitalized word.
	 * The replaceValue will match the case pattern of the matched string.
	 * For cases of 'cLieNt' that makes no sense, we won't consider them here
	 * @param replaceValue
	 * @returns replacer function
	 */
	private matchingCaseReplacer(
		replaceValue: string,
	): (match: string) => string {
		return (match: string) => {
			if (match.toUpperCase() === match) {
				// case of all capital letters (CLIENT)
				return replaceValue.toUpperCase();
			}
			if (match.toLowerCase() === match) {
				// case of all lower-case letters (client)
				return replaceValue.toLowerCase();
			}
			if (match.charAt(0).toUpperCase() === match.charAt(0)) {
				// case of capitalized word (Client)
				return StringUtils.capitalize(replaceValue);
			}
			return replaceValue;
		};
	}
}
